import { GenericCRUDService } from './genericCRUD';
import { CRUDService, DataItem } from '@/components/common/CRUDTable/types';

/**
 * Tenant Roadmaps CRUD Service
 * 
 * Handles CRUD operations for tenant roadmaps using the flexible CRUD framework.
 * This service provides roadmap-specific functionality for managing downloaded roadmaps.
 */
export class TenantRoadmapsCRUDService implements CRUDService<DataItem> {
  private genericService: GenericCRUDService;

  constructor() {
    // Use the specific tenant roadmaps endpoint that includes computed fields
    this.genericService = new GenericCRUDService({
      name: 'Tenant Roadmaps',
      displayName: 'My Roadmaps',
      displayNameSingular: 'Roadmap',
      apiEndpoint: '/tenant/roadmaps',
      routePath: '/tenant/roadmaps',
      idField: 'id',
      nameField: 'name',
      fields: [],
      tableColumns: [],
      searchFields: ['name', 'description', 'industry', 'functional_area', 'transformation_type'],
      defaultSort: { field: 'created_at', direction: 'desc' }
    });
  }

  /**
   * Get all tenant roadmaps
   */
  async getAll(params?: any): Promise<DataItem[]> {
    const response = await this.genericService.getAll(params);
    return response.data;
  }

  /**
   * Get a single roadmap by ID
   */
  async get(id: string): Promise<DataItem> {
    return this.genericService.getById(id);
  }

  /**
   * Get a single roadmap by ID (alias for get)
   */
  async getById(id: string): Promise<DataItem> {
    return this.genericService.getById(id);
  }

  /**
   * Create a new roadmap
   */
  async create(data: Partial<DataItem>): Promise<DataItem> {
    return this.genericService.create(data);
  }

  /**
   * Update an existing roadmap
   */
  async update(id: string, data: Partial<DataItem>): Promise<DataItem> {
    return this.genericService.update(id, data);
  }

  /**
   * Delete a roadmap
   */
  async delete(id: string): Promise<void> {
    return this.genericService.delete(id);
  }

  /**
   * Toggle roadmap status (ACTIVE/INACTIVE)
   */
  async toggleStatus(id: string, isActive: boolean): Promise<void> {
    await this.genericService.update(id, { status: isActive ? 'ACTIVE' : 'INACTIVE' });
  }

  /**
   * Archive a roadmap
   */
  async archiveRoadmap(id: string): Promise<void> {
    await this.genericService.update(id, { status: 'ARCHIVED' });
  }
}

// Export a singleton instance
export const tenantRoadmapsCRUDService = new TenantRoadmapsCRUDService();